import { alpha, type SxProps, type Theme } from '@mui/material';

const ROUNDED_BUTTON_RADIUS = 999;

const commentDialogInsetDivider = (theme: Theme) => ({
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    left: theme.spacing(3),
    right: theme.spacing(3),
    bottom: 0,
    height: '1px',
    backgroundColor: theme.palette.border.subtle,
    pointerEvents: 'none',
  },
});

export const commentCellStyles = {
  managerBox: (theme: Theme) => ({
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '40px',
    p: theme.spacing(0.5),
    borderRadius: theme.spacing(1),
    border: '1px dashed transparent',
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.light, 0.05),
      borderColor: 'primary.main',
      '& .action-icon': { opacity: 1 },
    },
  }),
  managerReadOnlyBox: (theme: Theme) => ({
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '40px',
    p: theme.spacing(0.5),
    borderRadius: theme.spacing(1),
    border: '1px dashed transparent',
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.light, 0.04),
      borderColor: 'transparent',
      '& .action-icon': { opacity: 1 },
    },
  }),
  managerText: {
    display: '-webkit-box',
    overflow: 'hidden',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 2,
    maxWidth: '140px',
    lineHeight: 1.2,
    textAlign: 'left',
  } as SxProps<Theme>,
  managerDialog: (theme: Theme) => ({
    '& .MuiBackdrop-root': {
      backgroundColor: alpha(theme.palette.common.black, 0.28),
      backdropFilter: 'none',
      WebkitBackdropFilter: 'none',
    },
    '& .MuiDialog-paper': {
      width: 482,
      maxWidth: 'calc(100vw - 32px)',
      margin: theme.spacing(2),
      borderRadius: theme.spacing(0.75),
      backgroundColor: theme.palette.common.white,
      boxShadow: theme.palette.shadow.dialog,
    },
  }),
  managerDialogHeader: (theme: Theme) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    px: theme.spacing(2.5),
    pt: theme.spacing(2.25),
    pb: theme.spacing(1),
  }),
  managerDialogTitle: {
    p: 0,
    color: 'text.primary',
    fontFamily: '"Nunito Sans", sans-serif',
    fontSize: '1.25rem',
    lineHeight: '24px',
    fontWeight: 800,
  } as SxProps<Theme>,
  managerDialogCloseIcon: (theme: Theme) => ({
    width: 32,
    height: 32,
    color: theme.palette.common.black,
    '&:hover': {
      backgroundColor: alpha(theme.palette.common.black, 0.04),
    },
  }),
  managerDialogContent: (theme: Theme) => ({
    px: theme.spacing(2.5),
    pt: 0,
    pb: theme.spacing(1),
  }),
  managerEditor: (theme: Theme) => ({
    overflow: 'hidden',
    border: `1px solid ${theme.palette.primary.main}`,
    borderRadius: theme.spacing(0.75),
    backgroundColor: theme.palette.common.white,
  }),
  managerEditorInput: (theme: Theme) => ({
    width: '100%',
    '& .MuiInputBase-root': {
      minHeight: 64,
      height: 'auto',
      p: 0,
      alignItems: 'flex-start',
      backgroundColor: theme.palette.common.white,
    },
    '&& .MuiInputBase-root': {
      height: 'auto',
    },
    '& .MuiInputBase-input': {
      boxSizing: 'border-box',
      px: theme.spacing(1.25),
      py: theme.spacing(1),
      color: theme.palette.text.primary,
      fontFamily: '"Nunito Sans", sans-serif',
      fontSize: '0.9375rem',
      lineHeight: '20px',
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
      resize: 'none',
    },
    '& .MuiInputBase-input::placeholder': {
      color: theme.palette.text.secondary,
      opacity: 0.85,
    },
    '& .MuiInputBase-input.Mui-disabled': {
      color: theme.palette.text.disabled,
      WebkitTextFillColor: theme.palette.text.disabled,
    },
  }),
  managerDialogActions: (theme: Theme) => ({
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
    px: theme.spacing(2.5),
    pt: 0,
    pb: theme.spacing(2.5),
  }),
  managerCancelButton: (theme: Theme) => ({
    minWidth: 100,
    height: 39,
    borderRadius: ROUNDED_BUTTON_RADIUS,
    borderColor: theme.palette.primary.main,
    color: theme.palette.primary.main,
    textTransform: 'none',
    fontFamily: '"Nunito Sans", sans-serif',
    fontSize: '0.9375rem',
    lineHeight: '20px',
    fontWeight: 700,
    '&:hover': {
      borderColor: theme.palette.primary.dark,
      backgroundColor: alpha(theme.palette.primary.main, 0.04),
    },
  }),
  managerSaveButton: (theme: Theme) => ({
    minWidth: 96,
    height: 39,
    borderRadius: ROUNDED_BUTTON_RADIUS,
    backgroundColor: theme.palette.primary.main,
    textTransform: 'none',
    fontFamily: '"Nunito Sans", sans-serif',
    fontSize: '0.9375rem',
    lineHeight: '20px',
    fontWeight: 700,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  }),
  managerReadOnlyDialog: (theme: Theme) => ({
    '& .MuiBackdrop-root': {
      backgroundColor: alpha(theme.palette.common.black, 0.28),
      backdropFilter: 'none',
      WebkitBackdropFilter: 'none',
    },
    '& .MuiDialog-paper': {
      width: 572,
      maxWidth: 'calc(100vw - 32px)',
      margin: theme.spacing(2),
      borderRadius: theme.spacing(1),
      backgroundColor: theme.palette.common.white,
      boxShadow: theme.palette.shadow.dialog,
    },
  }),
  managerReadOnlyTitle: (theme: Theme) => ({
    px: theme.spacing(3),
    pt: theme.spacing(3),
    pb: theme.spacing(1.5),
    color: 'text.primary',
    fontFamily: '"Nunito Sans", sans-serif',
    fontSize: '1.5rem',
    lineHeight: '28px',
    fontWeight: 800,
    ...commentDialogInsetDivider(theme),
  }),
  managerReadOnlyCommentText: {
    color: 'text.primary',
    fontFamily: '"Nunito Sans", sans-serif',
    fontSize: '1rem',
    lineHeight: '24px',
    fontWeight: 400,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  } as SxProps<Theme>,
  clientCell: (theme: Theme) => ({
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    minHeight: '40px',
    p: 0,
    borderRadius: theme.spacing(1),
    border: '1px dashed transparent',
    '&:hover': {
      backgroundColor: 'transparent',
      borderColor: 'transparent',
      '& .comment-details-button': {
        color: theme.palette.primary.dark,
      },
    },
  }),
  clientCellContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    minWidth: 0,
    maxWidth: '100%',
  } as SxProps<Theme>,
  clientCellText: {
    display: 'block',
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontFamily: '"Nunito Sans", sans-serif',
    fontSize: '1rem',
    lineHeight: '20px',
    fontWeight: 400,
  } as SxProps<Theme>,
  clientDetailsButton: (theme: Theme) => ({
    display: 'inline-flex',
    alignSelf: 'flex-start',
    mt: theme.spacing(0.25),
    color: theme.palette.primary.main,
    fontFamily: '"Nunito Sans", sans-serif',
    fontSize: '1rem',
    lineHeight: '20px',
    fontWeight: 400,
    textDecoration: 'underline',
    textUnderlineOffset: '2px',
  }),
  clientDialog: (theme: Theme) => ({
    '& .MuiBackdrop-root': {
      backgroundColor: alpha(theme.palette.common.black, 0.28),
      backdropFilter: 'none',
      WebkitBackdropFilter: 'none',
    },
    '& .MuiDialog-paper': {
      width: 572,
      maxWidth: 'calc(100vw - 32px)',
      margin: theme.spacing(2),
      borderRadius: theme.spacing(1),
      backgroundColor: theme.palette.common.white,
      boxShadow: theme.palette.shadow.dialog,
    },
  }),
  clientDialogHeader: (theme: Theme) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    px: theme.spacing(3),
    pt: theme.spacing(3),
    pb: theme.spacing(1.5),
    ...commentDialogInsetDivider(theme),
  }),
  clientDialogTitle: {
    color: 'text.primary',
    fontFamily: '"Nunito Sans", sans-serif',
    fontSize: '1.5rem',
    lineHeight: '28px',
    fontWeight: 800,
  } as SxProps<Theme>,
  clientDialogCloseIcon: (theme: Theme) => ({
    width: 32,
    height: 32,
    color: theme.palette.common.black,
    '&:hover': {
      backgroundColor: alpha(theme.palette.common.black, 0.04),
    },
  }),
  clientDialogContent: (theme: Theme) => ({
    px: theme.spacing(3),
    pt: theme.spacing(1.5),
    pb: theme.spacing(5.5),
  }),
  clientDialogText: {
    color: 'text.primary',
    fontFamily: '"Nunito Sans", sans-serif',
    fontSize: '1rem',
    lineHeight: '24px',
    fontWeight: 400,
    whiteSpace: 'pre-wrap',
  } as SxProps<Theme>,
  clientDialogActions: (theme: Theme) => ({
    justifyContent: 'center',
    px: theme.spacing(3),
    pb: theme.spacing(2),
    pt: 0,
  }),
  clientCloseButton: (theme: Theme) => ({
    minWidth: 106,
    height: 48,
    px: theme.spacing(3),
    borderRadius: ROUNDED_BUTTON_RADIUS,
    textTransform: 'none',
    fontFamily: '"Nunito Sans", sans-serif',
    fontSize: '1rem',
    lineHeight: '20px',
    fontWeight: 700,
    backgroundColor: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  }),
  empty: (theme: Theme) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    color: theme.palette.text.muted,
  }),
  addIcon: {
    fontSize: 16,
  } as SxProps<Theme>,
  infoIcon: (theme: Theme) => ({
    fontSize: 14,
    color: 'action.active',
    ml: theme.spacing(0.5),
    opacity: 0.6,
  }),
  viewIcon: (theme: Theme) => ({
    fontSize: 14,
    ml: theme.spacing(1),
    color: 'action.active',
    opacity: 0,
    transition: 'opacity 0.2s',
  }),
  editIcon: (theme: Theme) => ({
    fontSize: 14,
    ml: theme.spacing(1),
    color: 'primary.main',
    opacity: 0,
    transition: 'opacity 0.2s',
  }),
} as const;
